import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@shared/lib/cn';

/**
 * iOS-style list row. Title + optional subtitle, leading slot for
 * icon/avatar/image, trailing slot for chevron / value / switch.
 *
 * Used inside <ListGroup> for grouped lists and as a standalone tappable
 * row inside cards. Press feedback: row darkens 4% (handled by CSS).
 */

export interface ListItemProps {
  title: string;
  subtitle?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Hide chevron when neither nav nor sheet trigger. */
  noChevron?: boolean;
  onClick?: () => void;
  className?: string;
  /** Render as link or button shape. Default = button. */
  asChild?: boolean;
  disabled?: boolean;
  /** Highlighted state — used for "current tenant" / "selected" rows. */
  active?: boolean;
}

export function ListItem(
  { title, subtitle, leading, trailing, noChevron, onClick, className, disabled, active }: ListItemProps,
): JSX.Element {
  const interactive = !!onClick;
  const sharedClassName = cn(
    'group relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-fast',
    interactive ? 'active:bg-sunken hover:bg-sunken/60' : 'cursor-default',
    active && 'bg-primary-50',
    disabled && 'opacity-50 pointer-events-none',
    className,
  );

  const content = (
    <>
      {leading ? <div className="flex-shrink-0">{leading}</div> : null}
      <div className="min-w-0 flex-1">
        <p className={cn('truncate text-body text-ink', active && 'font-semibold text-primary-700')}>
          {title}
        </p>
        {subtitle ? <p className="truncate text-body-sm text-ink-secondary">{subtitle}</p> : null}
      </div>
      {trailing ? <div className="flex-shrink-0">{trailing}</div> : null}
      {interactive && !trailing && !noChevron ? (
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-ink-muted" strokeWidth={1.5} />
      ) : null}
    </>
  );

  if (!interactive) {
    return <div className={sharedClassName}>{content}</div>;
  }

  return (
    <button type="button" onClick={onClick} disabled={disabled} className={sharedClassName}>
      {content}
    </button>
  );
}
